import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Package, Globe, Zap, ArrowLeft, Clock, Eye, EyeOff } from 'lucide-react';
import AppLogoFull from '../components/AppLogoFull';
import LanguageSelector from '../components/LanguageSelector';
import ForcePasswordChange from '../components/ForcePasswordChange';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);
  const [resetSuccessMessage, setResetSuccessMessage] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const formLoadedAt = useRef(Date.now() / 1000);
  const { login, googleLogin } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const googleButtonRef = useRef(null);

  const handleGoogleCallback = useCallback(async (response) => {
    setError('');
    setGoogleLoading(true);
    const result = await googleLogin(response.credential);
    if (result.success) {
      if (result.force_password_change) {
        setShowForcePasswordChange(true);
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
    setGoogleLoading(false);
  }, [googleLogin, navigate]);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reset') === 'success') {
      setResetSuccessMessage(t('passwordResetSuccess'));
    }
  }, []);

  React.useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google?.accounts?.id) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleGoogleCallback,
    });

    if (googleButtonRef.current) {
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        width: '100%',
        locale: 'fr',
      });
    }
  }, [handleGoogleCallback]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password, {
      'x-hp-field': honeypot,
      'x-form-ts': String(formLoadedAt.current),
    });

    if (result.success) {
      if (result.force_password_change) {
        setShowForcePasswordChange(true);
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handlePasswordChanged = () => {
    setShowForcePasswordChange(false);
    navigate('/dashboard');
  };

  if (showForcePasswordChange) {
    return <ForcePasswordChange onPasswordChanged={handlePasswordChanged} />;
  }

  return (
    <div className="min-h-screen flex bg-[hsl(var(--background))]">
      <SEOHead
        title={t('seoLoginTitle')}
        description={t('seoLoginDesc')}
        noindex
      />
      {/* Left panel — hero image + branding overlay */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80&auto=format"
          alt="Logistics"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

        <button
          onClick={() => navigate('/')}
          className="absolute top-6 left-6 z-20 flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeft size={16} />
          {t('home')}
        </button>

        <div className="relative z-10 flex flex-col justify-end p-6 lg:p-10 w-full">
          <h2 className="text-2xl lg:text-3xl font-bold text-white leading-tight">
            {t('loginHeroTitle') || 'Simplifiez la gestion de votre fret'}
          </h2>
          <p className="mt-3 text-white/80 text-base max-w-md leading-relaxed">
            {t('loginAIDesc')}
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <div className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-2">
              <Package size={16} className="text-white/90 shrink-0" />
              <span className="text-sm text-white/90">{t('loginFeatureTracking')}</span>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-2">
              <Globe size={16} className="text-white/90 shrink-0" />
              <span className="text-sm text-white/90">{t('loginFeatureMultiCountry')}</span>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-2">
              <Zap size={16} className="text-white/90 shrink-0" />
              <span className="text-sm text-white/90">{t('loginFeatureNotifications')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex items-center justify-center p-4 relative">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>

        <Card className="w-full max-w-md border border-[hsl(var(--border))]">
          <CardHeader className="space-y-1 flex flex-col items-center">
            <AppLogoFull height={40} className="mb-4" />
            <CardDescription className="text-center">
              {t('loginSubtitle')}
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {resetSuccessMessage && (
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription className="text-green-800">
                    {resetSuccessMessage}
                  </AlertDescription>
                </Alert>
              )}

              {error && (
                error.includes('en attente de validation') || error.includes('attente de validation') ? (
                  <Alert className="bg-amber-50 border-amber-200">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      {error}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )
              )}
              {/* Honeypot — invisible to humans, caught by bots */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true" tabIndex={-1}>
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password">{t('password')}</Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium hover:underline text-[hsl(var(--accent))]"
                  >
                    {t('forgotPasswordTitle')}
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button type="submit" className="w-full" disabled={loading || googleLoading}>
                {loading ? t('loginLoading') : t('loginButton')}
              </Button>

              {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
                <>
                  <div className="flex items-center gap-3 w-full">
                    <div className="flex-1 h-px bg-[hsl(var(--border))]" />
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{t('or')}</span>
                    <div className="flex-1 h-px bg-[hsl(var(--border))]" />
                  </div>
                  <div ref={googleButtonRef} className="flex justify-center w-full" />
                </>
              )}

              <div className="text-sm text-center text-[hsl(var(--muted-foreground))]">
                {t('noAccount')}{' '}
                <Link to="/register-company" className="font-semibold hover:underline text-[hsl(var(--foreground))]">
                  {t('register')}
                </Link>
              </div>
              <div className="text-sm text-center space-y-2">
                <div>
                  <Link to="/track" className="text-[hsl(var(--accent))] hover:underline">
                    {t('trackPackageLink')}
                  </Link>
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  {t('newCompany')}{' '}
                  <Link to="/register-company" className="text-[hsl(var(--accent))] hover:underline font-medium">
                    {t('createMyCompanyLink')}
                  </Link>
                </div>
              </div>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Login;
